import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, AlertTriangle, CheckCircle2, Store, CalendarDays, Cloud, CloudOff, Loader2, Lock } from 'lucide-react';

import { diferencas, normalizarValor } from './gprs-diferencas';

// =====================================================================
// FINANCEIRO › Controle de GPRS — a GRADE DO MÊS (a tela de digitação).
//
// É o espelho da planilha que a loja preenchia no SharePoint: uma linha por dia
// do mês, três números digitados (equipamento · Fita POS · Flex) e duas
// diferenças que a tela calcula ENQUANTO se digita.
//
// ⚠️⚠️ SALVAMENTO AUTOMÁTICO (pedido da Julia, 16/09/2026) — NÃO HÁ BOTÃO SALVAR.
//    O dia é gravado ~1,2 s depois da última tecla (ou na hora, ao sair do
//    campo), e a coluna **Situação** diz de cada linha se ela está aguardando,
//    salvando, salva ou com erro. Três decisões sustentam isso, e as três estão
//    comentadas onde moram:
//      1. **diff contra o que o banco confirmou** — manda-se só o dia que mudou,
//         nunca a grade inteira (senão o rastro do mês todo seria reescrito por
//         quem mexeu num dia só);
//      2. **o snapshot só avança com resposta do servidor** — marcar otimista
//         faria uma falha de rede virar "salvo" com o número só na memória;
//      3. **rede de segurança na janela do debounce** — trocar de mês envia o
//         pendente, e fechar a aba pergunta antes.
//
// ⚠️⚠️ A CONTA NÃO MORA AQUI. Ela vem de `gprs-diferencas.js`, que é ESPELHO de
//    `server/financeiro/gprs/gprs-regras.js` e está travado pelo teste
//    `espelhos-back-front.test.js` (PAR 8). Escrever `b - c` direto no JSX é o
//    caminho curto para a tela e o servidor discordarem calados — que é
//    exatamente o bug que a planilha tinha (o sinal invertia depois do dia 1º).
//
// ⚠️ O dia FUTURO não aceita digitação, e quem decide isso é o servidor
//    (`linha.futuro` vem do `GET /grade`). Recalcular "é futuro?" aqui daria uma
//    segunda régua, e as duas divergiriam na virada do dia.
// =====================================================================

const API = '/api/financeiro/gprs';

// Paleta do módulo FINANCEIRO (verde-petróleo) — a fonte da verdade é o `cor=`
// do ModuloHeader no hub (docs/ARQUITETURA.md › Paleta dos módulos).
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/**
 * ⚠️⚠️ A PAUSA que dispara o salvamento — o relógio reinicia a CADA tecla, então
 *    quem salva é a pausa, não o tempo total. Sem o debounce, digitar "1234,56"
 *    dispararia oito requisições e oito atualizações de rastro para o mesmo dia.
 *    1,2 s foi escolhido para caber entre dois campos: quem passa de um valor ao
 *    seguinte com Tab quase nunca vê um envio no meio do caminho.
 */
const ESPERA_MS = 1200;

const brl = (n) =>
    n === null || n === undefined
        ? '—'
        : Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** 'AAAA-MM-DD' → '01/09 seg'. O dia da semana ajuda o gerente a se achar na grade. */
const rotuloDia = (iso) => {
    const [a, m, d] = String(iso).split('-').map(Number);
    const dt = new Date(a, m - 1, d);
    const semana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][dt.getDay()];
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')} ${semana}`;
};

const estiloInput = {
    padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6,
    fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit',
    width: '100%', boxSizing: 'border-box', textAlign: 'right',
    // ⚠️ `tabular-nums`: sem isso as colunas de dinheiro dançam a cada dígito e a
    //    grade de 30 linhas fica ilegível.
    fontVariantNumeric: 'tabular-nums',
};

/**
 * Cor da diferença: vermelho quando NÃO bate, cinza quando bate, neutro quando
 * ainda não dá para saber (algum valor em branco).
 * ⚠️ `null` (falta um lado) NUNCA é pintado de verde: "não preenchido" não pode
 *    se parecer com "conferido e bateu".
 */
const corDiferenca = (v) => {
    if (v === null || v === undefined) return '#94a3b8';
    return Math.abs(v) < 0.005 ? '#64748b' : '#b91c1c';
};

/**
 * O RASTRO do dia, em uma linha curta.
 *
 * ⚠️⚠️ "Lançado" e "editado" são mostrados SEPARADOS de propósito: o que
 *    interessa saber é se aquele número é o que a loja declarou ou se alguém
 *    mexeu depois — e um único "última alteração" apagaria essa diferença.
 *    Enquanto ninguém editou, `atualizadoEm` é nulo e a linha só diz quem lançou.
 * @returns {{texto:string, detalhe:string}|null} null = dia nunca salvo.
 */
const rastroDoDia = (l) => {
    if (!l?.registradoEm && !l?.atualizadoEm) return null;

    const quando = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const lancou = `Lançado por ${l.registradoPor || '—'} em ${quando(l.registradoEm)}`;
    if (!l.atualizadoEm) return { texto: l.registradoPor || '—', detalhe: lancou };

    return {
        // A tela mostra o ato MAIS RECENTE; o anterior fica no title (hover).
        texto: `${l.atualizadoPor || '—'} (editado)`,
        detalhe: `${lancou}\nEditado por ${l.atualizadoPor || '—'} em ${quando(l.atualizadoEm)}`,
    };
};

/** A marca da coluna Situação. Dia sem alteração nesta sessão não tem marca. */
const SITUACOES = {
    pendente: { icone: Loader2, cor: '#92400e', texto: 'aguardando' },
    salvando: { icone: Loader2, cor: '#0369a1', texto: 'salvando…' },
    salvo: { icone: CheckCircle2, cor: COR, texto: 'salvo' },
    erro: { icone: CloudOff, cor: '#b91c1c', texto: 'não salvou' },
};

const GprsGrade = () => {
    const [opcoes, setOpcoes] = useState(null);
    const [lojas, setLojas] = useState([]);
    const [loja, setLoja] = useState('');
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(new Date().getMonth() + 1);

    const [linhas, setLinhas] = useState([]);

    /**
     * ⚠️⚠️ O ESPELHO SÍNCRONO DE `linhas`, e ele existe por causa de um BUG REAL
     *    (medido em 16/09/2026, na primeira versão do salvamento automático): o
     *    envio lia o estado com `setLinhas((estado) => { atuais = estado; return
     *    estado; })`, na suposição de que o *updater* roda na hora. **Ele não
     *    roda** — no React 18 é agendado para o próximo render. O envio saía com a
     *    lista VAZIA, não tinha o que mandar, e o dia ficava parado em
     *    "aguardando" para sempre, sem erro nenhum.
     *
     * ⚠️ Quem lê isto é código que roda FORA do render (o temporizador do
     *    debounce, o `onBlur`, a limpeza do efeito) — e para esses o estado do
     *    React sempre chega atrasado. Por isso todo lugar que chama `setLinhas`
     *    atualiza este ref **na mesma linha**: se um deles esquecer, o envio volta
     *    a mandar valor velho, calado.
     */
    const linhasRef = useRef([]);

    /** Muda a grade na tela e no espelho, sempre juntos. */
    const aplicarLinhas = useCallback((proximas) => {
        linhasRef.current = proximas;
        setLinhas(proximas);
    }, []);
    const [carregando, setCarregando] = useState(true);
    const [podeDigitar, setPodeDigitar] = useState(false);
    // ⚠️ O PORQUÊ de não poder digitar, vindo do servidor. Campo cinza sem
    //    explicação vira chamado para o TI — e antes de 30/09 a tela inteira fica
    //    assim para as lojas.
    const [motivoSemDigitar, setMotivoSemDigitar] = useState('');

    // Erro da carga ou do último envio. O detalhe por dia mora no `erroPorDia`.
    const [erroGeral, setErroGeral] = useState('');

    // ⚠️⚠️ O SNAPSHOT DO QUE O BANCO CONFIRMOU, por dia — é o coração do
    //    salvamento automático. É contra ele que se decide O QUE MANDAR: só os
    //    dias cujos três valores divergem do salvo. Sem o diff, cada pausa na
    //    digitação reenviaria os 30 dias e reescreveria o rastro do mês inteiro
    //    (todo dia passaria a constar como "editado por" quem mexeu num só).
    // ⚠️ `useRef` e não `useState`: ele é lido dentro do temporizador do debounce,
    //    e como estado ficaria congelado no valor do render que agendou o envio.
    const salvoNoBanco = useRef(new Map());

    // Os dias alterados e ainda não confirmados pelo banco.
    const pendentes = useRef(new Set());
    const temporizador = useRef(null);

    // Situação POR DIA, e não uma mensagem global: com salvamento automático a
    // pessoa precisa saber QUAL linha não entrou — um aviso no topo sai do campo
    // de visão de quem está digitando no dia 23.
    const [statusDia, setStatusDia] = useState({});
    const [erroPorDia, setErroPorDia] = useState({});

    // --- carga inicial: o que a pessoa pode fazer + as lojas dela ---------
    useEffect(() => {
        (async () => {
            try {
                const [ro, rl] = await Promise.all([
                    fetch(`${API}/opcoes`, { headers: auth() }),
                    fetch(`${API}/lojas`, { headers: auth() }),
                ]);
                if (!ro.ok || !rl.ok) throw new Error('Não foi possível carregar a tela.');
                const o = await ro.json();
                const l = await rl.json();
                setOpcoes(o);
                setLojas(l);
                // Gerente tem uma loja só — já entra nela, sem um clique inútil.
                setLoja(o.escolheLoja ? (l[0] || '') : (o.lojaPropria || l[0] || ''));
            } catch (e) {
                setErroGeral(e.message || 'Erro ao carregar a tela.');
                setCarregando(false);
            }
        })();
    }, []);

    // --- a grade do mês ---------------------------------------------------
    const carregarGrade = useCallback(async () => {
        if (!loja) return;
        setCarregando(true);
        setErroGeral('');
        try {
            const q = new URLSearchParams({ loja, ano: String(ano), mes: String(mes) });
            const r = await fetch(`${API}/grade?${q}`, { headers: auth() });
            const dados = await r.json();
            if (!r.ok) throw new Error(dados?.message || 'Erro ao carregar a grade.');

            // ⚠️ Os valores viram STRING no estado, porque é isso que um `<input>`
            //    controlado guarda. O número só volta a existir na hora de calcular
            //    (via `normalizarValor`) e no corpo do POST.
            const comoTexto = (v) => (v === null || v === undefined ? '' : String(v));
            const prontas = dados.linhas.map((l) => ({
                ...l,
                equipamento: comoTexto(l.equipamento),
                fitaPos: comoTexto(l.fitaPos),
                flex: comoTexto(l.flex),
            }));
            aplicarLinhas(prontas);
            setPodeDigitar(!!dados.podeDigitar);
            setMotivoSemDigitar(dados.motivoSemDigitar || '');

            // O retrato do que está no banco AGORA — a régua do diff daqui em diante.
            salvoNoBanco.current = new Map(
                prontas.map((l) => [l.data, `${l.equipamento}|${l.fitaPos}|${l.flex}`])
            );
            pendentes.current.clear();
            setStatusDia({});
            setErroPorDia({});
        } catch (e) {
            setErroGeral(e.message || 'Erro ao carregar a grade.');
            aplicarLinhas([]);
        } finally {
            setCarregando(false);
        }
    }, [loja, ano, mes, aplicarLinhas]);

    useEffect(() => { carregarGrade(); }, [carregarGrade]);

    /**
     * O ENVIO — manda ao servidor só os dias que divergem do que o banco confirmou.
     *
     * ⚠️⚠️ **Dia em branco que está no envio É UM APAGAMENTO.** Se ele diverge do
     *    snapshot, é porque tinha número e a pessoa apagou — e o servidor grava
     *    `NULL` nos três, com rastro. Dia que sempre esteve vazio nunca entra no
     *    diff, então não vira linha de nada no banco.
     *
     * ⚠️ O snapshot só avança com o que o servidor CONFIRMOU. Marcá-lo otimista
     *    faria uma falha de rede apagar a pendência: a tela diria "salvo" e o
     *    número existiria só na memória do navegador.
     *
     * @param {string[]} [apenas] dias específicos; sem isso, todos os pendentes.
     */
    const enviar = useCallback(async (apenas) => {
        if (!loja || !podeDigitar) return;

        const alvos = apenas || [...pendentes.current];
        if (!alvos.length) return;

        // ⚠️⚠️ LÊ DO ESPELHO (`linhasRef`), NUNCA do estado `linhas`. Este código
        //    roda dentro de um temporizador agendado por um render ANTERIOR — a
        //    variável `linhas` daquele fechamento está congelada no valor de
        //    então, e mandaria uma tecla atrás do que está na tela.
        //    ⚠️ E não adianta ler por dentro de `setLinhas((estado) => …)`: foi
        //    exatamente isso que quebrou na primeira versão (o updater é agendado,
        //    não roda na hora — a lista saía vazia e o dia ficava em "aguardando").
        const porDia = new Map(linhasRef.current.map((l) => [l.data, l]));
        const aMandar = alvos
            .map((data) => porDia.get(data))
            .filter(Boolean)
            // ⚠️ Confere contra o snapshot na hora do envio, e não só na digitação:
            //    quem digitou e desfez (voltando ao valor salvo) não tem o que
            //    gravar — mandar assim mesmo carimbaria "editado por" à toa.
            .filter((l) => salvoNoBanco.current.get(l.data) !== `${l.equipamento}|${l.fitaPos}|${l.flex}`)
            .map((l) => ({ data: l.data, equipamento: l.equipamento, fitaPos: l.fitaPos, flex: l.flex }));

        if (!aMandar.length) {
            for (const data of alvos) pendentes.current.delete(data);
            return;
        }

        const marcar = (situacao) =>
            setStatusDia((e) => ({ ...e, ...Object.fromEntries(aMandar.map((l) => [l.data, situacao])) }));

        marcar('salvando');
        setErroGeral('');

        try {
            const r = await fetch(`${API}/grade`, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ loja, ano, mes, linhas: aMandar }),
            });
            const dados = await r.json();
            if (!r.ok) throw new Error(dados?.message || 'Erro ao salvar.');

            // Confirmado: o snapshot avança e os dias saem da fila de pendentes.
            for (const l of aMandar) {
                salvoNoBanco.current.set(l.data, `${l.equipamento}|${l.fitaPos}|${l.flex}`);
                pendentes.current.delete(l.data);
            }
            setErroPorDia((e) => {
                const novo = { ...e };
                for (const l of aMandar) delete novo[l.data];
                return novo;
            });

            // ⚠️ O rastro vem do servidor e é aplicado NA LINHA, sem recarregar a
            //    grade: recarregar brigaria com quem está digitando (o campo
            //    perderia o que foi teclado entre o envio e a resposta).
            if (Array.isArray(dados.linhas)) {
                const rastros = new Map(dados.linhas.map((x) => [x.data, x]));
                // ⚠️ Parte do espelho atual (e não de um estado capturado), senão o
                //    que a pessoa digitou DURANTE o envio seria sobrescrito.
                aplicarLinhas(
                    linhasRef.current.map((l) => (rastros.has(l.data) ? { ...l, ...rastros.get(l.data) } : l))
                );
            }
            marcar('salvo');
        } catch (e) {
            // ⚠️⚠️ Os dias CONTINUAM pendentes — é o que faz a próxima digitação (ou
            //    o botão "Tentar de novo") levá-los junto. Limpar a pendência aqui
            //    perderia o número sem ninguém perceber, que é o pior desfecho
            //    possível num salvamento automático: ninguém clicou em salvar, logo
            //    ninguém tem por que desconfiar.
            marcar('erro');
            const mensagem = e.message || 'Erro ao salvar.';
            setErroPorDia((atual) => ({ ...atual, ...Object.fromEntries(aMandar.map((l) => [l.data, mensagem])) }));
            setErroGeral(mensagem);
        }
    }, [loja, ano, mes, podeDigitar, aplicarLinhas]);

    // ⚠️ O envio mais recente num ref: o `beforeunload` e a limpeza do efeito de
    //    desmontagem rodam fora do ciclo de render e pegariam uma versão velha.
    const enviarRef = useRef(enviar);
    useEffect(() => { enviarRef.current = enviar; }, [enviar]);

    /** Digitou: guarda na tela, marca o dia como pendente e reagenda o envio. */
    const alterar = (data, campo, valor) => {
        aplicarLinhas(linhasRef.current.map((l) => (l.data === data ? { ...l, [campo]: valor } : l)));

        pendentes.current.add(data);
        setStatusDia((e) => ({ ...e, [data]: 'pendente' }));

        clearTimeout(temporizador.current);
        temporizador.current = setTimeout(() => enviarRef.current(), ESPERA_MS);
    };

    /** Saiu do campo: envia na hora, sem esperar a pausa. É o gesto de quem terminou. */
    const aoSairDoCampo = (data) => {
        if (!pendentes.current.has(data)) return;
        clearTimeout(temporizador.current);
        enviar([data]);
    };

    /**
     * ⚠️⚠️ A REDE DE SEGURANÇA DA JANELA DO DEBOUNCE. Entre a última tecla e o
     *    envio há 1,2 s em que o número existe **só na memória do navegador**. Se
     *    a pessoa troca de mês, sai da tela ou fecha a aba nessa janela, o
     *    trabalho some sem nenhum sintoma — e num salvamento automático ela nem
     *    tem por que desconfiar, porque nunca clicou em salvar.
     *      · trocar de loja/mês (ou sair da tela) → **envia o que está pendente**;
     *      · fechar a aba → o navegador **pergunta antes**.
     */
    useEffect(() => {
        // ⚠️ Copia as REFS (os objetos), nunca o `.current`. É o que satisfaz a
        //    regra `react-hooks/exhaustive-deps` sem quebrar o que esta limpeza
        //    precisa fazer: ela tem de ler a fila NO MOMENTO em que roda, e não o
        //    retrato de quando o efeito foi montado — congelar o `.current` aqui
        //    enviaria uma fila velha (ou vazia) e perderia a última digitação.
        const fila = pendentes;
        const relogio = temporizador;
        const enviarAgora = enviarRef;

        const aoFecharAba = (evento) => {
            if (!fila.current.size) return;
            evento.preventDefault();
            evento.returnValue = '';   // exigido pelo Chrome para o diálogo aparecer
        };
        window.addEventListener('beforeunload', aoFecharAba);
        return () => {
            window.removeEventListener('beforeunload', aoFecharAba);
            clearTimeout(relogio.current);
            if (fila.current.size) enviarAgora.current([...fila.current]);
        };
    }, [loja, ano, mes]);

    // --- o selo de situação do topo ----------------------------------------
    const situacaoGeral = useMemo(() => {
        const valores = Object.values(statusDia);
        if (Object.keys(erroPorDia).length) return 'erro';
        if (valores.includes('salvando')) return 'salvando';
        if (valores.includes('pendente')) return 'pendente';
        if (valores.includes('salvo')) return 'salvo';
        return null;
    }, [statusDia, erroPorDia]);

    const diasComErro = Object.keys(erroPorDia);

    // --- totais do rodapé --------------------------------------------------
    // ⚠️ A diferença do mês é a SOMA DAS DIFERENÇAS DIÁRIAS (só dos dias
    //    completos), nunca a diferença das somas: um dia com a Fita preenchida e
    //    a máquina em branco entraria na soma da Fita e faria a loja parecer
    //    devedora de um valor que ninguém declarou.
    const totais = useMemo(() => {
        const t = { equipamento: 0, fitaPos: 0, flex: 0, maquinaVsFita: 0, flexVsFita: 0, preenchidos: 0, incompletos: 0 };
        for (const l of linhas) {
            const v = {
                equipamento: normalizarValor(l.equipamento),
                fitaPos: normalizarValor(l.fitaPos),
                flex: normalizarValor(l.flex),
            };
            const vazia = v.equipamento === null && v.fitaPos === null && v.flex === null;
            if (vazia) continue;
            t.preenchidos += 1;
            t.equipamento += v.equipamento || 0;
            t.fitaPos += v.fitaPos || 0;
            t.flex += v.flex || 0;
            const d = diferencas(v);
            if (d.maquinaVsFita === null || d.flexVsFita === null) t.incompletos += 1;
            t.maquinaVsFita += d.maquinaVsFita || 0;
            t.flexVsFita += d.flexVsFita || 0;
        }
        return t;
    }, [linhas]);

    const anosDisponiveis = useMemo(() => {
        const atual = new Date().getFullYear();
        return [atual, atual - 1, atual - 2];
    }, []);

    const th = {
        padding: '10px 12px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase',
        color: COR_FORTE, background: COR_FUNDO, borderBottom: `2px solid ${COR_BORDA}`,
        textAlign: 'right', whiteSpace: 'nowrap',
    };
    const td = {
        padding: '4px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 14,
        textAlign: 'right', fontVariantNumeric: 'tabular-nums',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* ---------------- FILTROS ---------------- */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                {opcoes?.escolheLoja && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Store size={14} /> Loja
                        </span>
                        <select value={loja} onChange={(e) => setLoja(e.target.value)} style={{ ...estiloInput, textAlign: 'left', width: 180 }}>
                            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </label>
                )}

                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CalendarDays size={14} /> Competência
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <select value={mes} onChange={(e) => setMes(Number(e.target.value))} style={{ ...estiloInput, textAlign: 'left', width: 150 }}>
                            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                        <select value={ano} onChange={(e) => setAno(Number(e.target.value))} style={{ ...estiloInput, textAlign: 'left', width: 100 }}>
                            {anosDisponiveis.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                </label>

                {!opcoes?.escolheLoja && loja && (
                    <div style={{ padding: '8px 14px', background: COR_FUNDO, border: `1px solid ${COR_BORDA}`, borderRadius: 8, fontWeight: 800, color: COR_FORTE }}>
                        {loja}
                    </div>
                )}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                    {/* O SELO DE SALVAMENTO — ocupa o lugar do antigo botão "Salvar mês".
                        ⚠️ Ele é a única confirmação que a pessoa recebe: sem botão, o
                           silêncio se leria como "não fez nada". */}
                    {podeDigitar && situacaoGeral && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                            borderRadius: 8, fontSize: 13, fontWeight: 700,
                            background: situacaoGeral === 'erro' ? '#fef2f2' : COR_FUNDO,
                            border: `1px solid ${situacaoGeral === 'erro' ? '#fecaca' : COR_BORDA}`,
                            color: situacaoGeral === 'erro' ? '#b91c1c' : COR_FORTE,
                        }}>
                            {situacaoGeral === 'erro' ? (
                                <><CloudOff size={15} /> {diasComErro.length} dia(s) não salvaram</>
                            ) : situacaoGeral === 'salvo' ? (
                                <><Cloud size={15} /> Tudo salvo</>
                            ) : (
                                <><Loader2 size={15} /> Salvando…</>
                            )}
                        </div>
                    )}

                    {/* ⚠️ Só aparece quando há o que reenviar. Botão permanente traria
                           de volta a dúvida "preciso clicar?", que é o que o
                           salvamento automático veio tirar. */}
                    {podeDigitar && situacaoGeral === 'erro' && (
                        <button
                            onClick={() => enviar()}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
                                borderRadius: 8, border: 'none', fontWeight: 800, fontSize: 14, color: '#fff',
                                background: `linear-gradient(135deg, #16a39c 0%, ${COR} 100%)`, cursor: 'pointer',
                            }}
                        >
                            <RotateCcw size={16} /> Tentar de novo
                        </button>
                    )}

                    <button
                        onClick={carregarGrade}
                        disabled={carregando}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                            borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff',
                            color: '#475569', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                        }}
                    >
                        <RotateCcw size={16} /> Recarregar
                    </button>
                </div>
            </div>

            {/* ---------------- RECADOS ---------------- */}
            {/* ⚠️ A GRADE AINDA NÃO ABRIU para esta pessoa (ou ela não lança nesta
                   loja). Dito com todas as letras e com o que fazer enquanto isso —
                   sem este aviso a tela seria 30 linhas cinzentas sem motivo. */}
            {motivoSemDigitar && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, color: '#92400e', fontSize: 14 }}>
                    <Lock size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                        <strong>{motivoSemDigitar}</strong>
                        <br />
                        A grade abaixo está só para consulta — o que já foi lançado continua visível.
                    </span>
                </div>
            )}
            {erroGeral && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>
                    <AlertTriangle size={16} /> {erroGeral}
                    {diasComErro.length > 0 && (
                        <span style={{ fontWeight: 500 }}>
                            — o valor continua na tela e será reenviado; nada foi perdido.
                        </span>
                    )}
                </div>
            )}

            {/* ---------------- A GRADE ---------------- */}
            {carregando ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando a grade…</div>
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, textAlign: 'left' }}>Data</th>
                                <th style={th}>Venda — equipamentos</th>
                                <th style={th}>Venda GPRS — Fita POS</th>
                                <th style={th} title="Equipamento menos Fita POS">Dif. máquina × Fita</th>
                                <th style={th}>Venda GPRS — Flex</th>
                                <th style={th} title="Flex menos Fita POS">Dif. Flex × Fita</th>
                                <th style={{ ...th, textAlign: 'left' }}>Situação</th>
                                <th style={{ ...th, textAlign: 'left' }}>Rastro</th>
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map((l) => {
                                const d = diferencas({
                                    equipamento: normalizarValor(l.equipamento),
                                    fitaPos: normalizarValor(l.fitaPos),
                                    flex: normalizarValor(l.flex),
                                });
                                const rastro = rastroDoDia(l);
                                const situacao = SITUACOES[statusDia[l.data]];
                                const bloqueado = l.futuro || !podeDigitar;
                                const campo = (chave) => (
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={l[chave]}
                                        disabled={bloqueado}
                                        onChange={(e) => alterar(l.data, chave, e.target.value)}
                                        onBlur={() => aoSairDoCampo(l.data)}
                                        placeholder={l.futuro ? '' : '0,00'}
                                        style={{
                                            ...estiloInput,
                                            background: bloqueado ? '#f8fafc' : '#fff',
                                            color: bloqueado ? '#94a3b8' : '#0f172a',
                                            borderColor: erroPorDia[l.data] ? '#fca5a5' : '#cbd5e1',
                                        }}
                                    />
                                );
                                return (
                                    <tr key={l.data} style={{ background: erroPorDia[l.data] ? '#fef2f2' : (l.futuro ? '#fafafa' : '#fff') }}>
                                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: l.futuro ? '#94a3b8' : '#0f172a', whiteSpace: 'nowrap' }}>
                                            {rotuloDia(l.data)}
                                        </td>
                                        <td style={{ ...td, width: 150 }}>{campo('equipamento')}</td>
                                        <td style={{ ...td, width: 150 }}>{campo('fitaPos')}</td>
                                        <td style={{ ...td, fontWeight: 700, color: corDiferenca(d.maquinaVsFita) }}>{brl(d.maquinaVsFita)}</td>
                                        <td style={{ ...td, width: 150 }}>{campo('flex')}</td>
                                        <td style={{ ...td, fontWeight: 700, color: corDiferenca(d.flexVsFita) }}>{brl(d.flexVsFita)}</td>
                                        <td
                                            style={{ ...td, textAlign: 'left', fontSize: 12, fontWeight: 700, color: situacao?.cor || '#cbd5e1', whiteSpace: 'nowrap' }}
                                            title={erroPorDia[l.data] || ''}
                                        >
                                            {situacao ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                    <situacao.icone size={13} /> {situacao.texto}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td
                                            style={{ ...td, textAlign: 'left', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            title={rastro?.detalhe || ''}
                                        >
                                            {rastro?.texto || '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f8fafc', borderTop: `2px solid ${COR_BORDA}` }}>
                                <td style={{ ...td, textAlign: 'left', fontWeight: 800, color: COR_FORTE }}>
                                    Mês ({totais.preenchidos} dia(s) lançado(s))
                                </td>
                                <td style={{ ...td, fontWeight: 800 }}>{brl(totais.equipamento)}</td>
                                <td style={{ ...td, fontWeight: 800 }}>{brl(totais.fitaPos)}</td>
                                <td style={{ ...td, fontWeight: 800, color: corDiferenca(totais.maquinaVsFita) }}>{brl(totais.maquinaVsFita)}</td>
                                <td style={{ ...td, fontWeight: 800 }}>{brl(totais.flex)}</td>
                                <td style={{ ...td, fontWeight: 800, color: corDiferenca(totais.flexVsFita) }}>{brl(totais.flexVsFita)}</td>
                                <td style={td}></td>
                                <td style={td}></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* A pegadinha que o rodapé esconderia: um dia com dois dos três
                números preenchidos soma no total mas não produz diferença. */}
            {totais.incompletos > 0 && (
                <div style={{ fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={14} />
                    {totais.incompletos} dia(s) com número faltando — a diferença desses dias não entra no total do mês.
                </div>
            )}

            <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                As duas diferenças são calculadas pelo portal, sempre com a <strong>Fita POS como régua</strong>:
                “máquina × Fita” é o equipamento menos a Fita POS, e “Flex × Fita” é o Flex menos a Fita POS.
                Valor positivo significa que o outro sistema registrou <strong>mais</strong> que a Fita POS.
                Dias futuros não aceitam lançamento.
                <br />
                <strong>O lançamento é salvo sozinho</strong> — cada dia vai para o banco pouco depois de você parar
                de digitar, ou na hora em que sai do campo; a coluna <em>Situação</em> mostra o que já entrou. Não é
                preciso preencher o mês inteiro: lance os dias que tiver e volte depois. Apagar um valor já salvo
                <strong> limpa o dia</strong>, e a coluna <em>Rastro</em> guarda quem lançou e quem editou.
            </p>
        </div>
    );
};

export default GprsGrade;
